import React, { useState } from 'react';
import { MainLayout } from '@/components/layouts/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Smartphone, KeyRound, CheckCircle2, AlertCircle } from 'lucide-react';
import { use2FA } from '@/hooks/use2FA';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useNavigate } from 'react-router-dom';

export const Setup2FA: React.FC = () => {
  const navigate = useNavigate();
  const { status, enroll2FA, verify2FA } = use2FA();
  const [step, setStep] = useState<'intro' | 'scan' | 'verify'>('intro');
  const [qrCode, setQrCode] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [factorId, setFactorId] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleStartEnrollment = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await enroll2FA();
      setQrCode(result.qrCode);
      setSecret(result.secret);
      setFactorId(result.factorId);
      setStep('scan');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'initialisation du 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      setError('Le code doit contenir 6 chiffres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const success = await verify2FA(factorId, verificationCode);
      if (success) {
        setStep('verify');
        setTimeout(() => {
          navigate('/admin');
        }, 2000);
      } else {
        setError('Code invalide. Veuillez réessayer.');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la vérification');
    } finally {
      setLoading(false);
    }
  };

  if (status.loading) {
    return <LoadingSpinner message="Chargement..." />;
  }

  if (status.hasMFA && status.isEnrolled) {
    return (
      <MainLayout title="Authentification à Deux Facteurs">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
              <CardTitle>2FA Activé</CardTitle>
            </div>
            <CardDescription>
              Votre compte est protégé par l'authentification à deux facteurs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                L'authentification à deux facteurs est active et obligatoire pour tous les administrateurs.
              </AlertDescription>
            </Alert>
            <Button 
              onClick={() => navigate('/admin')}
              className="mt-4 w-full"
            >
              Retour au tableau de bord
            </Button>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Configuration 2FA Obligatoire">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Intro Step */}
        {step === 'intro' && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                <CardTitle>Configuration 2FA Requise</CardTitle>
              </div>
              <CardDescription>
                L'authentification à deux facteurs est obligatoire pour tous les administrateurs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Vous devez configurer l'authentification à deux facteurs pour accéder aux fonctionnalités administratives.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Prérequis
                </h3>
                <p className="text-sm text-muted-foreground">
                  Vous aurez besoin d'une application d'authentification sur votre téléphone :
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Google Authenticator</li>
                  <li>Microsoft Authenticator</li>
                  <li>Authy</li>
                  <li>Ou toute autre application TOTP compatible</li>
                </ul>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button 
                onClick={handleStartEnrollment}
                disabled={loading}
                className="w-full"
                size="lg"
              >
                {loading ? (
                  <>
                    <LoadingSpinner />
                    Initialisation...
                  </>
                ) : (
                  <>
                    <KeyRound className="mr-2 h-4 w-4" />
                    Configurer le 2FA
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Scan QR Code Step */}
        {step === 'scan' && (
          <Card>
            <CardHeader>
              <CardTitle>Étape 1 : Scanner le QR Code</CardTitle>
              <CardDescription>
                Utilisez votre application d'authentification pour scanner le code
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img 
                  src={qrCode} 
                  alt="QR Code 2FA" 
                  className="w-64 h-64"
                />
              </div>

              <div className="space-y-2">
                <Label>Ou entrez cette clé manuellement :</Label>
                <div className="flex gap-2">
                  <Input 
                    value={secret} 
                    readOnly 
                    className="font-mono"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(secret);
                    }}
                  >
                    Copier
                  </Button>
                </div>
              </div>

              <Alert>
                <AlertDescription>
                  Sauvegardez cette clé secrète dans un endroit sûr. Vous en aurez besoin si vous perdez l'accès à votre téléphone.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="code">Code de vérification (6 chiffres)</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setStep('intro');
                    setQrCode('');
                    setSecret('');
                    setFactorId('');
                    setVerificationCode('');
                    setError('');
                  }}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleVerify}
                  disabled={loading || verificationCode.length !== 6}
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner />
                      Vérification...
                    </>
                  ) : (
                    'Vérifier et Activer'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Success Step */}
        {step === 'verify' && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 text-green-500">
                <CheckCircle2 className="h-6 w-6" />
                <CardTitle>2FA Activé avec Succès !</CardTitle>
              </div>
              <CardDescription>
                Votre compte est maintenant sécurisé
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  L'authentification à deux facteurs est maintenant active. Vous devrez entrer un code depuis votre application d'authentification à chaque connexion.
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground text-center">
                Redirection vers le tableau de bord...
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};
